/**
 * RemindersPanel — the Planner artboard's right column.
 *
 * PLANNER-3. A field, an Add button, and the open reminders in five
 * groups: Overdue, Today, Upcoming, Later, Completed. The artboard's
 * own grouping, because it is the right one — a reminder list sorted
 * by date and nothing else makes you work out for yourself which of
 * them is late.
 *
 * A new reminder is due on the SELECTED day, and the hint under the
 * field says so. That is the artboard's behaviour and it is the
 * reason the field has no date picker: the calendar beside it already
 * is one.
 *
 * WHILE THE TABLE DOES NOT EXIST
 *
 * This ships before its migration can be applied — Rennan hands those
 * to Lovable. So the panel asks the service whether the table is
 * there and says what it is waiting for, once, quietly, instead of
 * throwing a toast on every page load.
 */
import { useMemo, useState } from 'react';
import { format, isToday } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  useCreateReminder,
  useReminders,
  useSetReminderDone,
  type ReminderRow,
} from '@/services/reminders';

/** Midnight today, for the day arithmetic every group leans on. */
const midnight = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const dayOf = (iso: string): Date => {
  // `due_on` is a DATE — "2026-10-29" — and `new Date("2026-10-29")`
  // parses as UTC midnight, which is the day before in every timezone
  // west of London. Split it instead.
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
};

interface Item {
  row: ReminderRow;
  /** Days from today. `Infinity` for a reminder with no date. */
  diff: number;
  when: string;
}

export function RemindersPanel({
  selected,
  rows: given,
}: {
  selected: Date;
  /** Supplied rows, for the marketing device. Nothing writes. */
  rows?: ReminderRow[];
}) {
  const { data, isLoading } = useReminders();
  const createReminder = useCreateReminder();
  const setDone = useSetReminderDone();
  const [draft, setDraft] = useState('');

  // Given rows are never pending: the marketing page must not show
  // the “this needs a migration” note.
  const pending = given ? false : (data?.pending ?? false);

  const { groups, openCount } = useMemo(() => {
    // Read inside the memo rather than as `data?.rows ?? []` above:
    // the fallback makes a new array every render, and the grouping
    // would redo itself on every keystroke in the field.
    const rows = given ?? data?.rows ?? [];
    const today = midnight();
    const items: Item[] = rows.map((row) => {
      if (!row.due_on) return { row, diff: Infinity, when: 'No date' };
      const day = dayOf(row.due_on);
      const diff = Math.round((day.getTime() - today.getTime()) / 86400000);
      const when =
        diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : diff === -1 ? 'Yesterday' : format(day, 'EEE, MMM d');
      return { row, diff, when };
    });
    items.sort((a, b) => a.diff - b.diff);

    const open = items.filter((i) => !i.row.done_at);
    const out: { name: string; cls?: string; items: Item[] }[] = [];
    const push = (name: string, list: Item[], cls?: string) => {
      if (list.length) out.push({ name, items: list, cls });
    };
    push('Overdue', open.filter((i) => i.diff < 0), 'overdue');
    push('Today', open.filter((i) => i.diff === 0));
    push('Upcoming', open.filter((i) => i.diff > 0 && i.diff <= 7));
    push('Later', open.filter((i) => i.diff > 7));
    push(
      'Completed',
      items.filter((i) => !!i.row.done_at),
    );
    return { groups: out, openCount: open.length };
  }, [data?.rows, given]);

  const add = async () => {
    if (given) return;
    const title = draft.trim();
    if (!title) return;
    try {
      await createReminder.mutateAsync({ title, due_on: format(selected, 'yyyy-MM-dd') });
      setDraft('');
    } catch (err) {
      toast.error((err as Error)?.message || "That didn't save");
    }
  };

  return (
    <section
      className="plan-panel"
      style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div
        style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 6px' }}
      >
        <div style={{ fontSize: 15, fontWeight: 600 }}>Reminders</div>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          {pending ? '' : `${openCount} open`}
        </div>
      </div>

      {pending ? (
        // Said once, flatly, rather than thrown as an error every time
        // the page loads. The column still shows what it will be.
        <p style={{ margin: 0, padding: '4px 6px 8px', fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
          Reminders need a table that isn’t in the database yet. Once{' '}
          <code style={{ fontSize: 11 }}>20261029140000_reminders.sql</code> has been applied, this
          column works without another deploy.
        </p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="plan-field"
              type="text"
              placeholder="New reminder"
              aria-label="New reminder"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void add();
              }}
            />
            <button
              type="button"
              className="plan-primary"
              onClick={() => void add()}
              disabled={!draft.trim() || createReminder.isPending}
              style={!draft.trim() ? { opacity: 0.45 } : undefined}
            >
              Add
            </button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', padding: '0 6px', marginTop: -4 }}>
            Due {isToday(selected) ? 'today' : format(selected, 'MMM d')} — the selected day
          </div>

          {isLoading && !given && (
            <p style={{ margin: 0, padding: '4px 6px', fontSize: 12, color: 'var(--muted)' }}>Loading…</p>
          )}

          {!isLoading && groups.length === 0 && (
            <p style={{ margin: 0, padding: '4px 6px', fontSize: 13, color: 'var(--muted)' }}>
              Nothing to remember. Add the first one above.
            </p>
          )}

          {groups.map((g) => (
            <div key={g.name} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div className={cn('plan-ghead', g.cls)}>{g.name}</div>
              {g.items.map(({ row, when }) => {
                const done = !!row.done_at;
                const id = `plan-rem-${row.id}`;
                return (
                  <div key={row.id} className="plan-row">
                    <input
                      type="checkbox"
                      className="plan-check"
                      id={id}
                      checked={done}
                      onChange={() => {
                        if (given) return;
                        setDone.mutate({ id: row.id, done: !done });
                      }}
                    />
                    <label
                      htmlFor={id}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        cursor: 'pointer',
                      }}
                    >
                      <span className={cn('plan-truncate', done && 'plan-done')} style={{ fontSize: 13 }}>
                        {row.title}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          color: 'var(--muted)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span className="plan-dot" style={{ background: 'var(--accent)' }} />
                        <span className="plan-truncate">
                          {when}
                          {row.due_at_time ? ` · ${row.due_at_time.slice(0, 5)}` : ''}
                        </span>
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>
          ))}
        </>
      )}
    </section>
  );
}

export default RemindersPanel;
